"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
var PearceKellyDetector_1 = require("./PearceKellyDetector");
var util_1 = require("./util");
var GenericGraphAdapter = (function () {
    function GenericGraphAdapter(mapConstructor, cycleDetector) {
        this.detector = cycleDetector;
        this.forward = new mapConstructor();
        this.backward = new mapConstructor();
        this.mapConstructor = mapConstructor;
        this.vertices = new mapConstructor();
        this.edgeCount = 0;
        this.adapter = {
            getData: this.getData.bind(this),
            getPredecessorsOf: this.getPredecessorsOf.bind(this),
            getSuccessorsOf: this.getSuccessorsOf.bind(this),
        };
    }
    GenericGraphAdapter.create = function (options) {
        if (options === void 0) { options = {}; }
        var mapConstructor = options.mapConstructor || Map;
        var detector = options.cycleDetector || new PearceKellyDetector_1.PearceKellyDetector();
        return new GenericGraphAdapter(mapConstructor, detector);
    };
    GenericGraphAdapter.prototype.map = function (vertexMapper, edgeDataMapper) {
        var _this = this;
        var clone = new GenericGraphAdapter(this.mapConstructor, util_1.DummyDetector);
        var clonedVertexMap = new this.mapConstructor();
        this.vertices.forEach(function (vertexData, vertex) {
            var clonedVertex = vertexMapper(vertex);
            clonedVertexMap.set(vertex, clonedVertex);
            clone.vertices.set(clonedVertex, vertexData);
        });
        this.forward.forEach(function (map, from) {
            var clonedMap = new _this.mapConstructor();
            var clonedFrom = clonedVertexMap.get(from);
            map.forEach(function (edgeData, to) {
                var clonedTo = clonedVertexMap.get(to);
                var clonedEdgeData = edgeData !== undefined ? edgeDataMapper(edgeData) : undefined;
                clonedMap.set(clonedTo, clonedEdgeData);
                clone.addToBackwardsMap(clonedFrom, clonedTo, clonedEdgeData);
            });
            clone.forward.set(clonedFrom, clonedMap);
        });
        clone.edgeCount = this.edgeCount;
        clone.detector = this.detector.map();
        return clone;
    };
    GenericGraphAdapter.prototype.clone = function (vertexCloner, edgeDataCloner) {
        var vCloner = vertexCloner !== undefined ? vertexCloner : function (vertex) { return vertex; };
        var eCloner = edgeDataCloner !== undefined ? edgeDataCloner : function (data) { return data; };
        return this.map(vCloner, eCloner);
    };
    GenericGraphAdapter.prototype.canContractEdge = function (from, to) {
        return util_1.canContractEdge(this, from, to);
    };
    GenericGraphAdapter.prototype.contractEdge = function (from, to, vertexMerger, edgeMerger) {
        return util_1.contractEdge(this, from, to, vertexMerger, edgeMerger);
    };
    GenericGraphAdapter.prototype.isReachable = function (source, target) {
        return this.detector.isReachable(this.adapter, source, target);
    };
    GenericGraphAdapter.prototype.getSuccessorsOf = function (vertex) {
        var f = this.forward.get(vertex);
        if (f === undefined) {
            return util_1.EmptyIterator;
        }
        return f.keys();
    };
    GenericGraphAdapter.prototype.getPredecessorsOf = function (vertex) {
        var b = this.backward.get(vertex);
        if (b === undefined) {
            return util_1.EmptyIterator;
        }
        return b.keys();
    };
    GenericGraphAdapter.prototype.getVertices = function () {
        return this.vertices.keys();
    };
    GenericGraphAdapter.prototype.getEdgeData = function (from, to) {
        var map = this.forward.get(from);
        if (!map) {
            return undefined;
        }
        return map.get(to);
    };
    GenericGraphAdapter.prototype.getEdgesWithDataTo = function (vertex) {
        var b = this.backward.get(vertex);
        if (b === undefined) {
            return util_1.EmptyIterator;
        }
        return b.entries();
    };
    GenericGraphAdapter.prototype.getEdgeDataTo = function (vertex) {
        var b = this.backward.get(vertex);
        if (b === undefined) {
            return util_1.EmptyIterator;
        }
        return util_1.createFilteredIterator(b.values());
    };
    GenericGraphAdapter.prototype.getEdgesWithDataFrom = function (vertex) {
        var f = this.forward.get(vertex);
        if (f === undefined) {
            return util_1.EmptyIterator;
        }
        return f.entries();
    };
    GenericGraphAdapter.prototype.getEdgeDataFrom = function (vertex) {
        var f = this.forward.get(vertex);
        if (f === undefined) {
            return util_1.EmptyIterator;
        }
        return util_1.createFilteredIterator(f.values());
    };
    GenericGraphAdapter.prototype.setEdgeData = function (from, to, data) {
        var f = this.forward.get(from);
        var b = this.backward.get(to);
        if (!f || !b || !f.has(to) || !b.has(from)) {
            return false;
        }
        f.set(to, data);
        b.set(from, data);
        return true;
    };
    GenericGraphAdapter.prototype.getEdges = function () {
        return util_1.createFlatMappedIterator(this.forward.entries(), function (entry) {
            return util_1.createMappedIterator(entry[1].keys(), function (key) { return [entry[0], key]; });
        });
    };
    GenericGraphAdapter.prototype.getEdgesWithData = function () {
        return util_1.createFlatMappedIterator(this.forward.entries(), function (entry) {
            return util_1.createMappedIterator(entry[1].entries(), function (subEntry) {
                return [entry[0], subEntry[0], subEntry[1]];
            });
        });
    };
    GenericGraphAdapter.prototype.getEdgeCount = function () {
        return this.edgeCount;
    };
    GenericGraphAdapter.prototype.supportsOrder = function () {
        return this.detector.supportsOrder();
    };
    GenericGraphAdapter.prototype.getOrder = function (vertex) {
        return this.detector.getOrder(this.adapter, vertex);
    };
    GenericGraphAdapter.prototype.getVertexCount = function () {
        return this.vertices.size;
    };
    GenericGraphAdapter.prototype.hasEdge = function (from, to) {
        var f = this.forward.get(from);
        return f ? f.has(to) : false;
    };
    GenericGraphAdapter.prototype.hasVertex = function (vertex) {
        return this.vertices.has(vertex);
    };
    GenericGraphAdapter.prototype.canAddEdge = function (from, to) {
        var f = this.forward.get(from);
        var b = this.backward.get(to);
        var didNotHaveFrom = this.addVertex(from);
        var didNotHaveTo = this.addVertex(to);
        if (!f) {
            this.forward.set(from, f = new this.mapConstructor());
        }
        if (!b) {
            this.backward.set(to, b = new this.mapConstructor());
        }
        if (f.has(to) || !this.detector.canAddEdge(this.adapter, from, to)) {
            if (didNotHaveFrom) {
                this.deleteVertex(from);
            }
            if (didNotHaveTo) {
                this.deleteVertex(to);
            }
            return false;
        }
        return true;
    };
    GenericGraphAdapter.prototype.addEdge = function (from, to, edgeData) {
        var f = this.forward.get(from);
        var b = this.backward.get(to);
        var didNotHaveFrom = this.addVertex(from);
        var didNotHaveTo = this.addVertex(to);
        if (!f) {
            this.forward.set(from, f = new this.mapConstructor());
        }
        if (!b) {
            this.backward.set(to, b = new this.mapConstructor());
        }
        if (f.has(to) || !this.detector.canAddEdge(this.adapter, from, to)) {
            if (didNotHaveFrom) {
                this.deleteVertex(from);
            }
            if (didNotHaveTo) {
                this.deleteVertex(to);
            }
            return false;
        }
        f.set(to, edgeData);
        b.set(from, edgeData);
        this.edgeCount += 1;
        return true;
    };
    GenericGraphAdapter.prototype.addVertex = function (vertex) {
        if (this.vertices.has(vertex)) {
            return false;
        }
        this.vertices.set(vertex, this.detector.createVertexData(this.adapter));
        return true;
    };
    GenericGraphAdapter.prototype.deleteEdge = function (from, to) {
        var f = this.forward.get(from);
        var b = this.backward.get(to);
        if (!f || !b) {
            return false;
        }
        if (!f.delete(to) || !b.delete(from)) {
            return false;
        }
        this.edgeCount -= 1;
        return true;
    };
    GenericGraphAdapter.prototype.deleteVertex = function (vertex) {
        if (!this.vertices.has(vertex)) {
            return false;
        }
        this.detector.onVertexDeletion(this.adapter, vertex);
        for (var it_1 = this.getSuccessorsOf(vertex), res = it_1.next(); !res.done; res = it_1.next()) {
            this.deleteEdge(vertex, res.value);
        }
        for (var it_2 = this.getPredecessorsOf(vertex), res = it_2.next(); !res.done; res = it_2.next()) {
            this.deleteEdge(res.value, vertex);
        }
        this.vertices.delete(vertex);
        return true;
    };
    GenericGraphAdapter.prototype.addToBackwardsMap = function (from, to, edgeData) {
        var map = this.backward.get(to);
        if (map === undefined) {
            this.backward.set(to, map = new this.mapConstructor());
        }
        map.set(from, edgeData);
    };
    GenericGraphAdapter.prototype.getData = function (key) {
        return this.vertices.get(key);
    };
    return GenericGraphAdapter;
}());
exports.GenericGraphAdapter = GenericGraphAdapter;
